/**
 * Payroll PCS — employee list / search (finance + superadmin).
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../AppHeader';
import { payrollAPI } from '../../services/api';

const emptyForm = {
  paycom_number: '',
  adp_number: '',
  first_name: '',
  last_name: '',
  date_of_hire: '',
  date_of_termination: '',
  pay_frequency: '',
  company: '',
};

const PayrollEmployeeList = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async (search = q) => {
    setLoading(true);
    setError('');
    try {
      const [empRes, coRes] = await Promise.all([
        payrollAPI.listEmployees({ q: search || undefined }),
        payrollAPI.listCompanyCodes(),
      ]);
      setEmployees(empRes.data.results || empRes.data || []);
      setCompanies(coRes.data.results || coRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load payroll employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        company: Number(form.company),
        adp_number: form.adp_number || null,
        date_of_hire: form.date_of_hire || null,
        date_of_termination: form.date_of_termination || null,
      };
      const res = await payrollAPI.createEmployee(payload);
      setDialogOpen(false);
      setForm(emptyForm);
      navigate(`/payroll/employees/${res.data.id}`);
    } catch (err) {
      const data = err.response?.data;
      setError(
        typeof data === 'object'
          ? Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join('; ')
          : 'Failed to create employee'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AppHeader backTo="/" />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5">Payroll allocation</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage Paycom employees and cost allocation maps (PCS)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate('/payroll/codes')}>
              Code tables
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
              Add employee
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search name or Paycom / ADP number"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load(q)}
            />
            <Button variant="outlined" startIcon={<Search />} onClick={() => load(q)}>
              Search
            </Button>
          </Box>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paycom #</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Hire</TableCell>
                  <TableCell>Term</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No payroll employees yet
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp) => (
                    <TableRow
                      key={emp.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/payroll/employees/${emp.id}`)}
                    >
                      <TableCell>{emp.paycom_number}</TableCell>
                      <TableCell>
                        {emp.last_name}, {emp.first_name}
                      </TableCell>
                      <TableCell>
                        {emp.company_code} — {emp.company_name}
                      </TableCell>
                      <TableCell>{emp.date_of_hire || '—'}</TableCell>
                      <TableCell>{emp.date_of_termination || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add payroll employee</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Paycom number"
            required
            value={form.paycom_number}
            onChange={(e) => setForm({ ...form, paycom_number: e.target.value })}
          />
          <TextField
            label="ADP number (legacy)"
            value={form.adp_number}
            onChange={(e) => setForm({ ...form, adp_number: e.target.value })}
          />
          <TextField
            label="First name"
            required
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
          <TextField
            label="Last name"
            required
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
          <TextField
            select
            label="Company"
            required
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.code} — {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Pay frequency"
            value={form.pay_frequency}
            onChange={(e) => setForm({ ...form, pay_frequency: e.target.value })}
          />
          <TextField
            label="Date of hire"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.date_of_hire}
            onChange={(e) => setForm({ ...form, date_of_hire: e.target.value })}
          />
          <TextField
            label="Date of termination"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.date_of_termination}
            onChange={(e) => setForm({ ...form, date_of_termination: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              saving ||
              !form.paycom_number ||
              !form.first_name ||
              !form.last_name ||
              !form.company
            }
            onClick={handleCreate}
          >
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PayrollEmployeeList;
